import {
    GeneratorType,
    LOAD_TYPE,
    SOLAR_TYPE,
    STORAGE_TYPE,
    WIND_TYPE
} from "./generatorType";
import { RegionType } from "./regionType";

export const MISO_SOLAR_DIRECTORY = "miso-solar";
export const MISO_WIND_DIRECTORY = "miso-wind";
export const MISO_STORAGE_DIRECTORY = "miso-storage";
export const MISO_LOAD_DIRECTORY = "miso-load";
export const PJM_DIRECTORY = "pjm";
export const PJM_LOAD_DIRECTORY = "pjm-load";
export const PJM_WIND_DIRECTORY = "pjm-wind";
export const PJM_STORAGE_DIRECTORY = "pjm-storage";
export const CAISO_DIRECTORY = "caiso";
export const SPP_SOLAR_DIRECTORY = "spp-solar";
export const SPP_WIND_DIRECTORY = "spp-wind";
export const SPP_LOAD_DIRECTORY = "spp-load";
export const ERCOT_SOLAR_DIRECTORY = "ercot-solar";
export const ERCOT_WIND_DIRECTORY = "ercot-wind";
export const ERCOT_STORAGE_DIRECTORY = "ercot-storage";
export const ERCOT_LOAD_DIRECTORY = "ercot-load";
export const WECC_SOLAR_DIRECTORY = "wecc-solar";
export const WECC_STORAGE_DIRECTORY = "wecc-storage";
export const WECC_LOAD_DIRECTORY = "wecc-load";
export const NYISO_SOLAR_DIRECTORY = "nyiso-solar";
export const NYISO_STORAGE_DIRECTORY = "nyiso-storage";
export const TVA_SOLAR_DIRECTORY = "tva-solar";
export const TVA_STORAGE_DIRECTORY = "tva-storage";
export const ISONE_SOLAR_DIRECTORY = "isone-solar";
export const ISONE_STORAGE_DIRECTORY = "isone-storage";
export const SOCO_SOLAR_DIRECTORY = "soco-solar";
export const SOCO_LOAD_DIRECTORY = "soco-load";
export const DUKE_SOLAR_DIRECTORY = "duke-solar";
export const DUKE_STORAGE_DIRECTORY = "duke-storage";
export const DUKE_LOAD_DIRECTORY = "duke-load";
export const CAISO_SOLAR_DIRECTORY = "caiso-solar";
export const CAISO_WIND_DIRECTORY = "caiso-wind";
export const CAISO_STORAGE_DIRECTORY = "caiso-storage";

export const URL_DIRECTORY_TYPES = [
    MISO_SOLAR_DIRECTORY,
    MISO_WIND_DIRECTORY,
    MISO_STORAGE_DIRECTORY,
    MISO_LOAD_DIRECTORY,
    PJM_DIRECTORY,
    PJM_LOAD_DIRECTORY,
    PJM_WIND_DIRECTORY,
    PJM_STORAGE_DIRECTORY,
    CAISO_DIRECTORY,
    SPP_SOLAR_DIRECTORY,
    SPP_WIND_DIRECTORY,
    SPP_LOAD_DIRECTORY,
    ERCOT_SOLAR_DIRECTORY,
    ERCOT_WIND_DIRECTORY,
    ERCOT_STORAGE_DIRECTORY,
    ERCOT_LOAD_DIRECTORY,
    WECC_SOLAR_DIRECTORY,
    WECC_STORAGE_DIRECTORY,
    WECC_LOAD_DIRECTORY,
    NYISO_SOLAR_DIRECTORY,
    NYISO_STORAGE_DIRECTORY,
    TVA_SOLAR_DIRECTORY,
    TVA_STORAGE_DIRECTORY,
    ISONE_SOLAR_DIRECTORY,
    ISONE_STORAGE_DIRECTORY,
    SOCO_SOLAR_DIRECTORY,
    SOCO_LOAD_DIRECTORY,
    DUKE_SOLAR_DIRECTORY,
    DUKE_STORAGE_DIRECTORY,
    DUKE_LOAD_DIRECTORY,
    CAISO_SOLAR_DIRECTORY,
    CAISO_WIND_DIRECTORY,
    CAISO_STORAGE_DIRECTORY
] as const;
export type UrlDirectoryType = (typeof URL_DIRECTORY_TYPES)[number];

export const isKnownUrlDirectoryType = (
    type: string
): type is UrlDirectoryType =>
    URL_DIRECTORY_TYPES.includes(type as UrlDirectoryType);

export const convertToUrlDirectoryType = (string: string): UrlDirectoryType => {
    if (isKnownUrlDirectoryType(string)) {
        return string;
    }

    throw new Error("Unexpected urlDirectory type");
};

export const REGION_TO_FUEL_TYPE_TO_URL_DICT: {
    [key in RegionType]: {
        [key in GeneratorType]: null | UrlDirectoryType;
    };
} = {
    NYISO: {
        solar: NYISO_SOLAR_DIRECTORY,
        storage: NYISO_STORAGE_DIRECTORY,
        wind: null,
        load: null
    },
    PJM: {
        solar: PJM_DIRECTORY,
        storage: PJM_STORAGE_DIRECTORY,
        wind: PJM_WIND_DIRECTORY,
        load: PJM_LOAD_DIRECTORY
    },
    MISO: {
        solar: MISO_SOLAR_DIRECTORY,
        storage: MISO_STORAGE_DIRECTORY,
        wind: MISO_WIND_DIRECTORY,
        load: MISO_LOAD_DIRECTORY
    },
    CAISO: {
        solar: CAISO_SOLAR_DIRECTORY,
        storage: CAISO_STORAGE_DIRECTORY,
        wind: CAISO_WIND_DIRECTORY,
        load: null
    },
    SPP: {
        solar: SPP_SOLAR_DIRECTORY,
        storage: null,
        wind: SPP_WIND_DIRECTORY,
        load: SPP_LOAD_DIRECTORY
    },
    ERCOT: {
        solar: ERCOT_SOLAR_DIRECTORY,
        storage: ERCOT_STORAGE_DIRECTORY,
        wind: ERCOT_WIND_DIRECTORY,
        load: ERCOT_LOAD_DIRECTORY
    },
    WECC: {
        solar: WECC_SOLAR_DIRECTORY,
        storage: WECC_STORAGE_DIRECTORY,
        wind: null,
        load: WECC_LOAD_DIRECTORY
    },
    TVA: {
        solar: TVA_SOLAR_DIRECTORY,
        storage: TVA_STORAGE_DIRECTORY,
        wind: null,
        load: null
    },
    ISONE: {
        solar: ISONE_SOLAR_DIRECTORY,
        storage: ISONE_STORAGE_DIRECTORY,
        wind: null,
        load: null
    },
    SOCO: {
        solar: SOCO_SOLAR_DIRECTORY,
        storage: null,
        wind: null,
        load: SOCO_LOAD_DIRECTORY
    },
    DUKE: {
        solar: DUKE_SOLAR_DIRECTORY,
        storage: DUKE_STORAGE_DIRECTORY,
        wind: null,
        load: DUKE_LOAD_DIRECTORY
    }
};

// TODO: Rewrite this to use REGION_TO_FUEL_TYPE_TO_URL_DICT above, which is hard to do while maintaining type-safety
export const getGeneratorTypeFromUrlDirectory = (
    urlDirectory: UrlDirectoryType
): GeneratorType => {
    const urlDirectoryToGeneratorType: {
        [key in UrlDirectoryType]: GeneratorType;
    } = {
        [MISO_SOLAR_DIRECTORY]: SOLAR_TYPE,
        [MISO_WIND_DIRECTORY]: WIND_TYPE,
        [MISO_STORAGE_DIRECTORY]: STORAGE_TYPE,
        [MISO_LOAD_DIRECTORY]: LOAD_TYPE,
        [PJM_DIRECTORY]: SOLAR_TYPE,
        [PJM_LOAD_DIRECTORY]: LOAD_TYPE,
        [PJM_WIND_DIRECTORY]: WIND_TYPE,
        [PJM_STORAGE_DIRECTORY]: STORAGE_TYPE,
        [CAISO_DIRECTORY]: SOLAR_TYPE,
        [SPP_SOLAR_DIRECTORY]: SOLAR_TYPE,
        [SPP_WIND_DIRECTORY]: WIND_TYPE,
        [SPP_LOAD_DIRECTORY]: LOAD_TYPE,
        [ERCOT_SOLAR_DIRECTORY]: SOLAR_TYPE,
        [ERCOT_WIND_DIRECTORY]: WIND_TYPE,
        [ERCOT_STORAGE_DIRECTORY]: STORAGE_TYPE,
        [ERCOT_LOAD_DIRECTORY]: LOAD_TYPE,
        [WECC_SOLAR_DIRECTORY]: SOLAR_TYPE,
        [WECC_STORAGE_DIRECTORY]: STORAGE_TYPE,
        [WECC_LOAD_DIRECTORY]: LOAD_TYPE,
        [NYISO_SOLAR_DIRECTORY]: SOLAR_TYPE,
        [NYISO_STORAGE_DIRECTORY]: STORAGE_TYPE,
        [TVA_SOLAR_DIRECTORY]: SOLAR_TYPE,
        [TVA_STORAGE_DIRECTORY]: STORAGE_TYPE,
        [ISONE_SOLAR_DIRECTORY]: SOLAR_TYPE,
        [ISONE_STORAGE_DIRECTORY]: STORAGE_TYPE,
        [SOCO_SOLAR_DIRECTORY]: SOLAR_TYPE,
        [SOCO_LOAD_DIRECTORY]: LOAD_TYPE,
        [DUKE_SOLAR_DIRECTORY]: SOLAR_TYPE,
        [DUKE_STORAGE_DIRECTORY]: STORAGE_TYPE,
        [DUKE_LOAD_DIRECTORY]: LOAD_TYPE,
        [CAISO_SOLAR_DIRECTORY]: SOLAR_TYPE,
        [CAISO_WIND_DIRECTORY]: WIND_TYPE,
        [CAISO_STORAGE_DIRECTORY]: STORAGE_TYPE
    };

    return urlDirectoryToGeneratorType[urlDirectory];
};

// TODO: Rewrite this to use REGION_TO_FUEL_TYPE_TO_URL_DICT above, which is hard to do while maintaining type-safety
export const getRegionFromUrlDirectory = (
    urlDirectory: UrlDirectoryType
): RegionType => {
    const urlDirectoryToRegionType: {
        [key in UrlDirectoryType]: RegionType;
    } = {
        [MISO_SOLAR_DIRECTORY]: RegionType.MISO,
        [MISO_WIND_DIRECTORY]: RegionType.MISO,
        [MISO_STORAGE_DIRECTORY]: RegionType.MISO,
        [MISO_LOAD_DIRECTORY]: RegionType.MISO,
        [PJM_DIRECTORY]: RegionType.PJM,
        [PJM_LOAD_DIRECTORY]: RegionType.PJM,
        [PJM_WIND_DIRECTORY]: RegionType.PJM,
        [PJM_STORAGE_DIRECTORY]: RegionType.PJM,
        [CAISO_DIRECTORY]: RegionType.CAISO,
        [SPP_SOLAR_DIRECTORY]: RegionType.SPP,
        [SPP_WIND_DIRECTORY]: RegionType.SPP,
        [SPP_LOAD_DIRECTORY]: RegionType.SPP,
        [ERCOT_SOLAR_DIRECTORY]: RegionType.ERCOT,
        [ERCOT_WIND_DIRECTORY]: RegionType.ERCOT,
        [ERCOT_STORAGE_DIRECTORY]: RegionType.ERCOT,
        [ERCOT_LOAD_DIRECTORY]: RegionType.ERCOT,
        [WECC_SOLAR_DIRECTORY]: RegionType.WECC,
        [WECC_STORAGE_DIRECTORY]: RegionType.WECC,
        [WECC_LOAD_DIRECTORY]: RegionType.WECC,
        [NYISO_SOLAR_DIRECTORY]: RegionType.NYISO,
        [NYISO_STORAGE_DIRECTORY]: RegionType.NYISO,
        [TVA_SOLAR_DIRECTORY]: RegionType.TVA,
        [TVA_STORAGE_DIRECTORY]: RegionType.TVA,
        [ISONE_SOLAR_DIRECTORY]: RegionType.ISONE,
        [ISONE_STORAGE_DIRECTORY]: RegionType.ISONE,
        [SOCO_SOLAR_DIRECTORY]: RegionType.SOCO,
        [SOCO_LOAD_DIRECTORY]: RegionType.SOCO,
        [DUKE_SOLAR_DIRECTORY]: RegionType.DUKE,
        [DUKE_STORAGE_DIRECTORY]: RegionType.DUKE,
        [DUKE_LOAD_DIRECTORY]: RegionType.DUKE,
        [CAISO_SOLAR_DIRECTORY]: RegionType.CAISO,
        [CAISO_WIND_DIRECTORY]: RegionType.CAISO,
        [CAISO_STORAGE_DIRECTORY]: RegionType.CAISO
    };

    return urlDirectoryToRegionType[urlDirectory];
};
